// eslint-disable-next-line import/no-anonymous-default-export
import React, { useState, useEffect, useRef } from 'react';
import { SelectBox } from 'devextreme-react';
import moment from 'moment-timezone';
import {
  DropDownShockThreshold,
  MergedChart,
  // DropDownTemperature,
  // TemperatureChart,
  CyclesChart,
} from './mechnicalAttributes';
import './mechanical.css';
import AssetsService from 'api/assets.service';
import 'leaflet-draw/dist/leaflet.draw-src.css';
import 'leaflet/dist/leaflet.css';
import { DateBox } from 'devextreme-react/date-box';
import Toast from 'components/Toast';
import DataGrid from 'devextreme-react/data-grid';

const MechanicalHeatMap = (props) => {
  const assetId = props.assetId;
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState('Last 24 hours');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const dateBoxRefStartMech = useRef(null);
  const dateBoxRefEndMech = useRef(null);
  const tenant = props.tenant;
  const [eventData, setEventData] = useState([]);
  const [temperatureData, setTemperatureData] = useState([]);
  const [cyclesData, setCyclesData] = useState([]);
  const [selectedThresholdValue, setSelectedThresholdValue] = useState(2000);
  const [thresholdValue, setThresholdValue] = useState(2000);
  const [maxThresholdValue, setMaxThresholdValue] = useState(16000);
  const [minThresholdValue, setMinThresholdValue] = useState(0);
  const [error, setError] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [loadingText, setLoadingText] = useState('Loading...');
  const EXTRA_BOTTOM = '100px';
  const MRP_SOLUTIONS_TENANT_ID = '0fcc4e26-8d5c-409c-a60c-a4a87c20d416';
  const MOLDFORM_TENANT_ID = '468c693b-f62b-40a5-a536-e41dbef902ed';
  const KABOOM_LABS_ID = 'f51ff9cb-5b92-4730-8844-159f1df4ee6f';
  const MOLDFORMINGDEMO_BENOIT_TENANT_ID = '3ecae8b8-3221-431e-b4f1-44009de1619e';
  const MOLDFORMINGDEMO_JOSE_TENANT_ID = 'ec00faad-2740-4ca1-be9c-8d83680b402c';
  const REDLORE_TENANT_ID = '19e7c189-4b94-46dd-939b-bc5236c4800b';
  const telemetryVisible =
    tenant === MRP_SOLUTIONS_TENANT_ID ||
    tenant === MOLDFORM_TENANT_ID ||
    tenant === KABOOM_LABS_ID ||
    tenant === MOLDFORMINGDEMO_BENOIT_TENANT_ID ||
    tenant === MOLDFORMINGDEMO_JOSE_TENANT_ID ||
    tenant === REDLORE_TENANT_ID;
  const [toastConfig, setToastConfig] = React.useState({
    isVisible: false,
    type: 'info',
    message: '',
    displayTime: 15000,
  });

  async function handleDataChanges() {
    await setEventData([]);
    await setTemperatureData([]);
    await setCyclesData([]);
    setLoadingText('Loading...');
  }

  const defaultValues = {
    minInWHSeats: null,
    maxInWHSeats: null,
    minOutWHSeats: null,
    maxOutWHSeats: null,
    minInFastCars: null,
    maxInFastCars: null,
    minOutFastCars: null,
    maxOutFastCars: null,
    minTransFastCars: null,
    maxTransFastCars: null,
    minTransSeats: null,
    maxTransSeats: null,
  };

  const generateJSONFromRecord = (record) => {
    const messageMapping = {
      'Incoming WH at Seats': { min: 'minInWHSeats', max: 'maxInWHSeats' },
      'Outgoing WH at Seats': { min: 'minOutWHSeats', max: 'maxOutWHSeats' },
      'Incoming WH at FastCars': { min: 'minInFastCars', max: 'maxInFastCars' },
      'Outgoing WH at FastCars': { min: 'minOutFastCars', max: 'maxOutFastCars' },
      'Transit to FastCars': { min: 'minTransFastCars', max: 'maxTransFastCars' },
      'Transit to Seats': { min: 'minTransSeats', max: 'maxTransSeats' },
    };

    const mapped = messageMapping[record.message] || {};

    return [
      {
        date: new Date(record.time_from),
        [mapped.min]: 5000,
        [mapped.max]: 6000,
        showLabel: true,
      },
      {
        date: new Date(record.time_to),
        [mapped.min]: 5000,
        [mapped.max]: 6000,
        showLabel: false,
      },
    ];
  };

  const generateJSON = (arrA) => {
    return arrA.flatMap((record) => generateJSONFromRecord(record));
  };

  function updateBottomProperty(arr) {
    let shouldSetBottom = false;
    let count = 0;

    arr.forEach((item) => {
      if (item.showLabel === true) {
        count++;
        shouldSetBottom = count % 2 === 0;
        item.bottom = shouldSetBottom;
      } else {
        item.bottom = item.bottom || false;
      }
    });

    return arr;
  }

  async function getData() {
    try {
      console.log('in get data');

      // 1. Better date validation
      const rawFrom = localStorage.getItem(`${tenant}dateFilterFrom`);
      const rawTo = localStorage.getItem(`${tenant}dateFilterTo`);

      const fromMoment = moment(rawFrom).utc();
      const toMoment = moment(rawTo).utc();

      if (!fromMoment.isValid() || !toMoment.isValid()) return true;

      const from = fromMoment.format();
      const to = toMoment.format();
      console.log('from, to', from, to);

      // 2. Clean Await usage
      const response = await AssetsService.locationHistoryMechanical(assetId, from, to);
      const result = response?.data?.data;

      if (!result) {
        setLoadingText('No Data');
        return;
      }

      // Always set temperature if it exists to avoid repeating it in every 'if' block
      if (result.temperatureData) {
        setTemperatureData(result.temperatureData);
      }

      // 3. Logic branching
      if (result.history?.length > 0 && tenant !== REDLORE_TENANT_ID) {
        let jsonArray = generateJSON(result.events);

        // Fixed property naming: changed 'date' to 'time' to match sorting logic
        const convertedArrB = result.history.map((record) => ({
          time: record.time, // Changed from 'date' to 'time'
          level: record.level,
        }));

        jsonArray = updateBottomProperty(jsonArray);

        // Removed unnecessary 'await' from sync array methods
        const combinedArray = jsonArray.concat(convertedArrB);
        const sortedArr = combinedArray.sort((a, b) => new Date(a.time) - new Date(b.time));

        setEventData(sortedArr);

        // Handle specific empty data cases for certain tenants
        if (
          result.temperatureData?.length === 0 &&
          [MOLDFORM_TENANT_ID, KABOOM_LABS_ID, REDLORE_TENANT_ID].includes(tenant)
        ) {
          setEventData([]);
          setLoadingText('No Data');
        }
      } else if (result.cyclesData?.length) {
        setCyclesData(result.cyclesData);
      } else if (!result.temperatureData?.length) {
        // If no history, no cycles, and no temperature
        setEventData([]);
        setLoadingText('No Data');
      }
    } catch (error) {
      console.error('Error fetching mechanical data:', error);
      setLoadingText('Error loading data');
    }
  }
  // async function getData() {
  //   console.log("in get data")
  //   // await handleDataChanges();
  //   const from = moment(localStorage.getItem(`${tenant}dateFilterFrom`))
  //     .utc()
  //     .format();
  //   const to = moment(localStorage.getItem(`${tenant}dateFilterTo`))
  //     .utc()
  //     .format();
  //     console.log("from, to",from, to)
  //   if (from === 'Invalid date' || to === 'Invalid date') return true;
  //   if (from !== null && to !== null) {

  //     await AssetsService.locationHistoryMechanical(assetId, from, to).then(async (value) => {
  //       setTemperatureData(value.data.data.temperatureData);
  //       console.log("value.data.data.temperatureData",value.data.data.temperatureData)
  //       // console.log("from, to",from, to)
  //       if (value?.data?.data?.history.length > 0 && tenant !== REDLORE_TENANT_ID) {
  //         let jsonArray = generateJSON(value.data.data.events);
  //         let convertedArrB = value.data.data.history.map((record) => ({
  //           date: new Date(record.time),
  //           level: record.level,
  //         }));
  //         jsonArray = updateBottomProperty(jsonArray);
  //         let combinedArray = await jsonArray.concat(convertedArrB);
  //         let sortedArr = await combinedArray.sort((a, b) => new Date(a.time) - new Date(b.time));
  //         setEventData(sortedArr);
  //         setTemperatureData(value.data.data.temperatureData);
  //         if (
  //           value?.data?.data?.temperatureData?.length === 0 &&
  //           (tenant === MOLDFORM_TENANT_ID || tenant === KABOOM_LABS_ID || tenant === REDLORE_TENANT_ID)
  //         ) {
  //           setEventData([]);
  //           setLoadingText('No Data');
  //         }
  //       } else if (value?.data?.data?.cyclesData?.length) {
  //         setCyclesData(value.data.data.cyclesData);
  //         if (value?.data?.data?.temperatureData?.length) {
  //           setTemperatureData(value.data.data.temperatureData);
  //         }
  //       } else if (value?.data?.data?.temperatureData?.length) {
  //         setTemperatureData(value.data.data.temperatureData);
  //       } else {
  //         setEventData([]);
  //         setLoadingText('No Data');
  //       }
  //     });
  //   }
  // }

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      getData();
    }
  }, [startDate, endDate]);

  const handleStartDate = () => {
    dateBoxRefStartMech.current.instance().open();
    setSelectedValue('Custom');
  };

  const handleEndDate = () => {
    dateBoxRefEndMech.current.instance().open();
    setSelectedValue('Custom');
  };

  function onThresholdValueChanged(e) {
    if (e.value === 0) {
      setShowInput(true);
    } else {
      setShowInput(false);
    }
    setSelectedThresholdValue(e.value);
    setThresholdValue(e.value);
  }

  const handleStartDateChange = async (value) => {
    await setStartDate(value);
    if (value && endDate && moment(value).isAfter(endDate)) {
      await setEndDate(moment(value).add(1, 'days').toDate());
      localStorage.setItem(`${tenant}dateFilterTo`, moment(value).add(1, 'days').toDate());
    }
    localStorage.setItem(`${tenant}dateFilterFrom`, moment(value).format());

    if (endDate) {
      localStorage.setItem(`${tenant}dateFilterTo`, moment(endDate).format());
    }
    getData();
  };

  const handleEndDateChange = async (value) => {
    await setEndDate(value);
    if (value && startDate && moment(value).isBefore(startDate)) {
      await setStartDate(moment(value).subtract(1, 'days').toDate());
      localStorage.setItem(`${tenant}dateFilterFrom`, moment(value).subtract(1, 'days').toDate());
    }
    localStorage.setItem(`${tenant}dateFilterTo`, moment(value).format());
    if (startDate) {
      localStorage.setItem(`${tenant}dateFilterFrom`, moment(startDate).format());
    }

    getData();
  };

  const handleValueChange = async (value) => {
    setSelectedValue(value);
    console.log('handleValueChange', value);
    if (value === 'Custom') {
      console.log('in custom');
      localStorage.setItem(`${tenant}dateFilterText`, value);
    } else {
      console.log('in else');
      setStartDate(null);
      setEndDate(null);
      let start, end;
      switch (value) {
        case 'Last 24 hours':
          start = moment().subtract(24, 'hours').format();
          end = moment().format();
          break;
        case 'Today':
          console.log('in today');
          start = moment().startOf('day').format();
          end = moment().endOf('day').format();
          break;
        case 'Last 5 min':
          start = moment().subtract(5, 'minutes').format();
          end = moment().format();
          break;
        case 'Last 20 min':
          start = moment().subtract(20, 'minutes').format();
          end = moment().format();
          break;
        case 'Last 1 hour':
          start = moment().subtract(1, 'hour').format();
          end = moment().format();
          break;
        case 'Last 4 hours':
          start = moment().subtract(4, 'hours').format();
          end = moment().format();
          break;
        case 'Last 12 hours':
          start = moment().subtract(12, 'hours').format();
          end = moment().format();
          break;
        case 'Yesterday':
          start = moment().subtract(1, 'day').startOf('day').format();
          end = moment().subtract(1, 'day').endOf('day').format();
          break;
        case 'This Week':
          start = moment().startOf('week').format();
          end = moment().endOf('week').format();
          break;
        case 'Last Week':
          start = moment().subtract(1, 'weeks').startOf('week').format();
          end = moment().subtract(1, 'weeks').endOf('week').format();
          break;
        case 'This Month':
          start = moment().startOf('month').format();
          end = moment().endOf('month').format();
          break;
        case 'Last Month':
          start = moment().subtract(1, 'month').startOf('month').format();
          end = moment().subtract(1, 'month').endOf('month').format();
          break;
        case 'Last 7 days':
          start = moment().subtract(7, 'days').format();
          end = moment().format();
          break;
        default:
          break;
      }
      console.log('start', start);
      console.log('end', end);
      setStartDate(start);
      setEndDate(end);
      localStorage.setItem(`${tenant}dateFilterText`, value);
    }
  };

  const handleCustomThreshold = async (value) => {
    if (value > minThresholdValue && value <= maxThresholdValue) {
      setError(null);
    } else {
      setError(`Threshold value should be between ${minThresholdValue} - ${maxThresholdValue} mG`);
      return false;
    }
    setThresholdValue(parseInt(value));
  };

  // useEffect(() => {
  //   console.log('mechanical called');
  //   const periodVal = localStorage.getItem(`${tenant}dateFilterText`);
  //   if (periodVal !== null && periodVal !== 'null') {
  //     console.log('period val: ', periodVal);
  //     if (periodVal === 'Custom') {
  //       const startDate = moment(localStorage.getItem(`${tenant}dateFilterFrom`));
  //       const endDate = moment(localStorage.getItem(`${tenant}dateFilterTo`));
  //       setStartDate(startDate);
  //       setEndDate(endDate);
  //     }
  //     handleValueChange(periodVal);
  //   } else {
  //     handleValueChange('Last 24 hours');
  //   }
  //   getData();
  // }, [props.selectedTabIndex]);

  // useEffect(async () => {
  //   setLoading(true);
  // }, [props.dateRange]);

  useEffect(() => {
    // 1. RE-INITIALIZE LOGIC (Your original code)
    const periodVal = localStorage.getItem(`${tenant}dateFilterText`);

    if (periodVal !== null && periodVal !== 'null') {
      if (periodVal === 'Custom') {
        const storedStart = localStorage.getItem(`${tenant}dateFilterFrom`);
        const storedEnd = localStorage.getItem(`${tenant}dateFilterTo`);
        if (storedStart) setStartDate(moment(storedStart));
        if (storedEnd) setEndDate(moment(storedEnd));
      }
      handleValueChange(periodVal);
    } else {
      handleValueChange('Last 24 hours');
    }

    // 2. DATA FETCHING & RESIZE LOGIC
    // If we have no data, go get it.
    if (temperatureData.length === 0 && cyclesData.length === 0 && eventData.length === 0) {
      getData();
    } else {
      /* CRITICAL FIX: If data ALREADY exists, the charts are already rendered 
         but likely have 0px width because the tab was hidden. 
         We wait 150ms for the TabPanel to finish showing the div, 
         then trigger a window resize to force ECharts to recalculate.
      */
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 150);
    }
  }, [props.selectedTabIndex]);

  return (
    <>
      <Toast setParentToastConfig={setToastConfig} toastBody={toastConfig} />
      <div className="assetMechanicalContainer">
        <div className="btnContainer">
          <div className="dateBtn">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SelectBox
                style={{ marginRight: '10px', width: '20%' }}
                dataSource={[
                  'Today',
                  'Last 5 min',
                  'Last 20 min',
                  'Last 1 hour',
                  'Last 4 hours',
                  'Last 12 hours',
                  'Last 24 hours',
                  'Yesterday',
                  'This Week',
                  'Last Week',
                  'This Month',
                  'Last Month',
                  'Last 7 days',
                  'Custom',
                ]}
                value={selectedValue}
                onValueChanged={(e) => handleValueChange(e.value)}
                labelMode="static"
                label="Period"
                stylingMode="outlined"
              />
              <div onClick={() => handleStartDate()}>
                <DateBox
                  ref={dateBoxRefStartMech}
                  style={{ marginRight: '10px' }}
                  type="datetime"
                  value={startDate}
                  onValueChanged={async (e) => await handleStartDateChange(e.value)}
                  placeholder="Start Date"
                  labelMode="static"
                  label="Start Date"
                  stylingMode="outlined"
                  displayFormat="yyyy-MM-dd HH:mm"
                  valueFormat="yyyy-MM-dd HH:mm"
                />
              </div>
              <div onClick={() => handleEndDate()}>
                <DateBox
                  ref={dateBoxRefEndMech}
                  style={{ marginRight: '10px' }}
                  type="datetime"
                  value={endDate}
                  onValueChanged={async (e) => await handleEndDateChange(e.value)}
                  placeholder="End Date"
                  labelMode="static"
                  label="End Date"
                  stylingMode="outlined"
                  displayFormat="yyyy-MM-dd HH:mm"
                  valueFormat="yyyy-MM-dd HH:mm"
                />
              </div>
            </div>
          </div>
        </div>

        {eventData.length > 0 || temperatureData.length > 0 || cyclesData.length > 0 ? (
          <>
            <div className="mechanical-container btnContainer">
              {tenant !== KABOOM_LABS_ID &&
                tenant !== MOLDFORM_TENANT_ID &&
                tenant !== REDLORE_TENANT_ID &&
                tenant !== MOLDFORMINGDEMO_BENOIT_TENANT_ID &&
                tenant !== MOLDFORMINGDEMO_JOSE_TENANT_ID &&
                eventData.length > 0 &&
                tenant !== MRP_SOLUTIONS_TENANT_ID && (
                  <div className="chart-row">
                    <div className="side-content">
                      <DropDownShockThreshold
                        onValueChanged={(event) => onThresholdValueChanged(event)}
                        selectedThresholdValue={selectedThresholdValue}
                        showInput={showInput}
                        OnTextChange={(value) => handleCustomThreshold(value)}
                        error={error}
                      />
                    </div>
                    <MergedChart
                      width="100%"
                      eventData={eventData}
                      thresholdValue={thresholdValue}
                    />
                  </div>
                )}

              {/* {temperatureData.length > 0 &&  cyclesData.length <= 0 &&(
                <div
                  className="chart-row"
                  style={
                    tenant === MRP_SOLUTIONS_TENANT_ID ||
                    tenant === MOLDFORM_TENANT_ID ||
                    tenant === KABOOM_LABS_ID
                      ? { marginBottom: EXTRA_BOTTOM, marginTop: EXTRA_BOTTOM }
                      : {}
                  }
                >
                  {console.log("cyclesData",cyclesData)}
                  <TemperatureChart
                    width="100%"
                    eventData={temperatureData}
                    thresholdValue={thresholdValue}
                  />
                </div>
              )} */}
              {/* {cyclesData.length > 0 && ( */}
              <div className="chart-row-three">
                <CyclesChart
                  width="100%"
                  eventData={cyclesData}
                  temperatureData={temperatureData}
                />
              </div>
              {/* )} */}
            </div>
          </>
        ) : (
          <section className="column_two">
            <DataGrid
              height={'calc(100vh - 329px)'}
              columnAutoWidth={true}
              allowColumnResizing={true}
              showBorders={true}
              noDataText={loadingText}
            ></DataGrid>
          </section>
        )}
      </div>
    </>
  );
};
export default MechanicalHeatMap;
